import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { RedirectIfAuthed, RequireAuth, RequireSuperAdmin } from './app/RequireAuth';
import { AppShell } from './components/AppShell';
import { ToastProvider } from './components/Toast';
import { PlanLimitProvider } from './components/PlanLimitDialog';
import { AuthPage } from './pages/AuthPage';
import { ForgotPasswordPage, ResetPasswordPage, VerifyEmailPage } from './pages/AuthFlowPages';
import { InvitePage } from './pages/InvitePage';
import { LandingPage } from './pages/LandingPage';
import { PrivacyPage, TermsPage } from './pages/LegalPages';
import { ProjectPage } from './pages/ProjectPage';
import { ProjectsIndex } from './pages/ProjectsIndex';
import { ProofReviewPage } from './pages/ProofReviewPage';
import { ProfilePage } from './pages/ProfilePage';
import { PricingPage } from './pages/PricingPage';
import { RecyclePage } from './pages/RecyclePage';
import { SettingsPage } from './pages/SettingsPage';
import { SharePage } from './pages/SharePage';
import { AdminLayout } from './pages/admin/AdminLayout';
import { AdminLoginPage } from './pages/admin/AdminLoginPage';
import { AdminOverviewPage } from './pages/admin/AdminOverviewPage';
import { AdminAccountsPage } from './pages/admin/AdminAccountsPage';
import { AdminIntegrationsPage } from './pages/admin/AdminIntegrationsPage';

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <RedirectIfAuthed>
        <LandingPage />
      </RedirectIfAuthed>
    ),
  },
  {
    path: '/login',
    element: (
      <RedirectIfAuthed>
        <AuthPage mode="login" />
      </RedirectIfAuthed>
    ),
  },
  {
    path: '/register',
    element: (
      <RedirectIfAuthed>
        <AuthPage mode="register" />
      </RedirectIfAuthed>
    ),
  },
  {
    // Invite acceptance — reachable signed in or out (it routes to auth itself).
    path: '/invite/:token',
    element: <InvitePage />,
  },
  { path: '/verify-email', element: <VerifyEmailPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  // External review link. Public by design and deliberately outside AppShell —
  // a guest sees one PDF and nothing else of the account.
  { path: '/s/:token', element: <SharePage /> },
  { path: '/privacy', element: <PrivacyPage /> },
  { path: '/terms', element: <TermsPage /> },
  {
    // App shell (icon rail + project sidebar) wraps the project views.
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { path: '/app', element: <ProjectsIndex /> },
      { path: '/projects/:projectId', element: <ProjectPage /> },
      // User-facing term is "PDF"; /proofs/ kept as a back-compat alias for old links.
      { path: '/pdfs/:proofId', element: <ProofReviewPage /> },
      { path: '/proofs/:proofId', element: <ProofReviewPage /> },
      { path: '/recycle-bin', element: <RecyclePage /> },
      { path: '/profile', element: <ProfilePage /> },
      { path: '/pricing', element: <PricingPage /> },
      { path: '/workspaces/:workspaceId/settings', element: <SettingsPage /> },
    ],
  },
  {
    path: '/admin/login',
    element: (
      <RedirectIfAuthed>
        <AdminLoginPage />
      </RedirectIfAuthed>
    ),
  },
  {
    path: '/admin',
    element: (
      <RequireSuperAdmin>
        <AdminLayout />
      </RequireSuperAdmin>
    ),
    children: [
      { index: true, element: <AdminOverviewPage /> },
      { path: 'accounts', element: <AdminAccountsPage /> },
      { path: 'integrations', element: <AdminIntegrationsPage /> },
    ],
  },
]);

export function App() {
  return (
    <ToastProvider>
      <PlanLimitProvider>
        <RouterProvider router={router} />
      </PlanLimitProvider>
    </ToastProvider>
  );
}
